// DeleteCategoryModal.tsx — Co Marca — Eliminar Categoría
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Trash2, X, AlertTriangle } from 'lucide-react';
import { deleteCategory } from '../../services/categoryService';

interface DeleteCategoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    category: { id: number; nombre: string } | null;
    onDeleted: () => void;
}

const colors = {
    primary:        '#c1348b',
    primaryDark:    '#8f1f64',
    secondary:      '#61257b',
    secondaryLight: '#8b3fa8',
    accentDark:     '#ea8406',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');
.dm-modal * { font-family: 'DM Sans', sans-serif; box-sizing: border-box; }
@keyframes dmFadeIn  { from { opacity:0; } to { opacity:1; } }
@keyframes dmSlideUp { from { opacity:0; transform:translateY(20px) scale(.97); } to { opacity:1; transform:translateY(0) scale(1); } }
@keyframes dmShake   { 0%,100% { transform:translateX(0); } 20%,60% { transform:translateX(-4px); } 40%,80% { transform:translateX(4px); } }
@keyframes spin { to { transform:rotate(360deg); } }
.dm-overlay { animation: dmFadeIn  .2s ease both; }
.dm-dialog  { animation: dmSlideUp .3s cubic-bezier(.16,1,.3,1) both; }
.dm-btn-delete {
    display:inline-flex; align-items:center; gap:7px;
    background:linear-gradient(135deg,${colors.primary},${colors.primaryDark});
    color:#fff; border:none; border-radius:11px;
    padding:10px 22px; font-size:.88rem; font-weight:800;
    cursor:pointer; box-shadow:0 4px 14px rgba(193, 52, 139,.28);
    transition:all .22s cubic-bezier(.16,1,.3,1);
}
.dm-btn-delete:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 8px 22px rgba(193, 52, 139,.36); }
.dm-btn-delete:disabled { background:#e2e8f0; color:#94a3b8; box-shadow:none; cursor:not-allowed; }
.dm-btn-cancel {
    display:inline-flex; align-items:center; gap:7px;
    background:#f8fafc; border:1.5px solid #e2e8f0;
    color:#374151; border-radius:11px;
    padding:10px 18px; font-size:.88rem; font-weight:700;
    cursor:pointer; transition:all .2s;
}
.dm-btn-cancel:hover { background:#f1f5f9; }
`;

const DeleteCategoryModal: React.FC<DeleteCategoryModalProps> = ({ isOpen, onClose, category, onDeleted }) => {
    const [isLoading, setIsLoading] = useState(false);

    const handleConfirm = async () => {
        if (!category) return;
        setIsLoading(true);
        try {
            await deleteCategory(category.id);
            onDeleted();
        } catch (error) {
            console.error('Error al eliminar la categoría:', error);
        } finally {
            setIsLoading(false);
            onClose();
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <>
            <style>{css}</style>
            <div className="dm-modal" style={{ position:'fixed', inset:0, zIndex:99999, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
                {/* Overlay */}
                <div className="dm-overlay" onClick={onClose} style={{ position:'absolute', inset:0, background:'rgba(15,23,42,.6)', backdropFilter:'blur(6px)' }}/>

                {/* Dialog */}
                <div className="dm-dialog" style={{ position:'relative', background:'#fff', borderRadius:'24px', width:'100%', maxWidth:'440px', boxShadow:'0 32px 80px rgba(0,0,0,.20)', border:'1.5px solid #f1f5f9', overflow:'hidden' }}>

                    {/* Barra top roja */}
                    <div style={{ height:'4px', background:`linear-gradient(90deg,${colors.primary},${colors.primaryDark},${colors.primary}80)`, borderRadius:'24px 24px 0 0' }}/>

                    {/* Header */}
                    <div style={{ padding:'24px 24px 0', display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                            {/* Icono de alerta */}
                            <div style={{ width:'44px', height:'44px', borderRadius:'14px', background:`${colors.primary}12`, border:`1.5px solid ${colors.primary}22`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                <AlertTriangle size={20} color={colors.primary}/>
                            </div>
                            <div>
                                <h3 style={{ fontSize:'1.05rem', fontWeight:'900', color:'#0f172a', letterSpacing:'-.02em' }}>Confirmar eliminación</h3>
                                <p style={{ fontSize:'.72rem', color:'#94a3b8', fontWeight:'500', marginTop:'2px' }}>Esta acción no se puede deshacer</p>
                            </div>
                        </div>
                        <button onClick={onClose} style={{ width:'30px', height:'30px', borderRadius:'9px', border:'1.5px solid #f1f5f9', background:'#f8fafc', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0 }}
                            onMouseEnter={e=>{ (e.currentTarget as HTMLButtonElement).style.background='#f1f5f9'; }}
                            onMouseLeave={e=>{ (e.currentTarget as HTMLButtonElement).style.background='#f8fafc'; }}>
                            <X size={14} color="#64748b"/>
                        </button>
                    </div>

                    {/* Body */}
                    <div style={{ padding:'20px 24px 24px' }}>
                        {/* Barra de colores */}
                        <div style={{ display:'flex', gap:'4px', marginBottom:'20px' }}>
                            {[colors.primary, colors.secondary, colors.accentDark, colors.secondaryLight].map((c,i)=>(
                                <div key={i} style={{ height:'2px', width:i===0?'18px':'6px', borderRadius:'2px', background:c, opacity:.5 }}/>
                            ))}
                        </div>

                        {/* Mensaje */}
                        <p style={{ fontSize:'.9rem', color:'#475569', lineHeight:1.65, marginBottom:'16px' }}>
                            ¿Estás seguro que deseas eliminar la categoría{' '}
                            <strong style={{ color:colors.primaryDark, fontWeight:'900' }}>"{category?.nombre}"</strong>?
                        </p>

                        {/* Card de advertencia */}
                        <div style={{ background:`${colors.primary}07`, border:`1.5px solid ${colors.primary}18`, borderRadius:'12px', padding:'12px 14px', display:'flex', alignItems:'flex-start', gap:'10px', marginBottom:'24px' }}>
                            <Trash2 size={14} color={colors.primary} style={{ flexShrink:0, marginTop:'1px' }}/>
                            <p style={{ fontSize:'.78rem', color:colors.primaryDark, fontWeight:'600', lineHeight:1.5 }}>
                                Se eliminará permanentemente la categoría y todos los datos asociados a ella.
                            </p>
                        </div>

                        {/* Botones */}
                        <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px' }}>
                            <button onClick={onClose} className="dm-btn-cancel">Cancelar</button>
                            <button onClick={handleConfirm} disabled={isLoading} className="dm-btn-delete">
                                {isLoading
                                    ? <><span style={{ width:'13px', height:'13px', border:'2px solid rgba(255,255,255,.4)', borderTopColor:'#fff', borderRadius:'50%', animation:'spin .7s linear infinite', display:'inline-block' }}/> Eliminando...</>
                                    : <><Trash2 size={14}/> Eliminar</>
                                }
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};

export default DeleteCategoryModal;